const { theme } = require('../theme');
const imageProxyService = require('./imageProxyService');

const backgroundFocalPointConfig = new Map(
  Object.entries({
    lefttop: 'left top',
    top: 'center top',
    righttop: 'right top',
    left: 'left center',
    right: 'right center',
    leftbottom: 'left bottom',
    bottom: 'center bottom',
    rightbottom: 'right bottom',
    center: 'center center'
  })
);

const qualityMap = { default: null, high: 90, extreme: 100 };

const imageResizerService = {
  // Defaults to { width: 16, height: 9 }
  getAspectRatioFromEntity: entity =>
    entity?.mediaImage?.height && entity.mediaImage.width
      ? { width: entity.mediaImage.width, height: entity.mediaImage.height }
      : { width: 16, height: 9 },

  getAspectRatioFromString: aspect => {
    const [width, height] = aspect.toLowerCase().split('x');
    return { width: parseInt(width, 10), height: parseInt(height, 10) };
  },

  getWidth: (imageData, height, aspectRatio = null) => {
    if (imageData.height && imageData.width) {
      if (aspectRatio) {
        return Math.ceil(height * (aspectRatio.height / aspectRatio.width));
      }
      return Math.ceil((height * imageData.width) / imageData.height);
    }
    return undefined;
  },

  getHeight: (imageData, width, aspectRatio = null) => {
    if (imageData.height && imageData.width) {
      if (aspectRatio) {
        return Math.ceil(width * (aspectRatio.height / aspectRatio.width));
      }
      return Math.ceil((width * imageData.height) / imageData.width);
    }
    return undefined;
  },

  getResizedImage: (
    mediaImage,
    width,
    options = {
      aspectRatio: null,
      position: null,
      quality: null
    }
  ) => {
    // Check if originalImageData contains all necessary data
    if (!mediaImage.url) {
      const errorMessage =
        "'imageData' parameter must be an object with 'url' property.";
      // eslint-disable-next-line
      console.log(errorMessage, mediaImage);
      throw new Error(errorMessage);
    }
    const height = imageResizerService.getHeight(
      mediaImage,
      width,
      options.aspectRatio
    );
    return {
      url: imageResizerService.getStyleUrl(
        mediaImage.url,
        width,
        height,
        options
      ),
      width,
      height
    };
  },

  getStyleUrl: (
    url,
    width,
    height,
    options = {
      position: null,
      quality: null
    }
  ) => {
    if (!imageResizerService.isStylable(url)) {
      const errorMessage = `url "${url}" must contain "/files/".`;
      // eslint-disable-next-line
      console.log(errorMessage, url);
      throw new Error(errorMessage);
    }

    const params = [];
    if (width) {
      params.push(`w=${width}`);
    }
    if (height) {
      params.push(`h=${height}`);
    }
    if (options.position) {
      params.push(`p=${options.position}`);
    }
    if (options.quality) {
      params.push(`q=${options.quality}`);
    }
    const query = params.join('&');

    return `${imageProxyService.getCanonicalUrl(
      url.replace('/files/', `/files/styles/image_3200/public/`)
    )}${query ? `?${query}` : ''}`;
  },

  getStyleUrlsByBreakpoint: (
    url,
    options = {
      position: null,
      quality: null
    }
  ) => {
    const styleUrlsByBreakpoint = {};

    theme.breakpoints.sorted.forEach(bp => {
      styleUrlsByBreakpoint[bp.name] = imageResizerService.getStyleUrl(
        url,
        bp.width,
        null,
        options
      );
    });

    return styleUrlsByBreakpoint;
  },

  isStylable: url => typeof url === 'string' && url.indexOf('/files/') !== -1,

  isLandscape: entity => entity.height < entity.width,

  isPortrait: entity => entity.height > entity.width,

  getOrientation: entity => {
    if (imageResizerService.isLandscape(entity)) {
      return 'landscape';
    }
    if (imageResizerService.isPortrait(entity)) {
      return 'portrait';
    }
    return 'square';
  },
  // There is an edge case where an entity is properly formed but
  // contains, due to Drupal's fault:
  // a) an incorrect url.
  // b) an incorrect width.
  isValidEntity: entity => {
    if (!entity?.mediaImage?.url || entity.mediaImage.url === '/') {
      return false;
    }
    return true;
  },

  getImageEntityQuality: quality => {
    return qualityMap[quality] || null;
  },

  overrideImageFocalPoint: entity => {
    const image = entity?.entity;
    if (entity?.focalPoint && entity?.focalPoint !== '') {
      image.focalPoint = entity.focalPoint;
    }
    return image;
  },

  getBackgroundFocalPoint: focalPoint =>
    backgroundFocalPointConfig.has(focalPoint)
      ? backgroundFocalPointConfig.get(focalPoint)
      : backgroundFocalPointConfig.get('center')
};

module.exports.imageResizerService = imageResizerService;
